/**
 * Единицы измерения текста. 02-contracts.md §3, §Хребет данных 1.5.
 *
 * ЕДИНСТВЕННОЕ место в репозитории, где берётся .length у текста поста и где пишется [...s].
 * Единицы разные и путать их нельзя: лимит 4096/1024 — в КОДПОИНТАХ, offset'ы сущностей —
 * в UTF-16 code units. 'привет 👋' даёт 8 кодпоинтов против 9 code units.
 *
 * Два уровня защиты, оба проверены компилятором:
 *   1. брендированные ЧИСЛА для величин, с которыми нужна арифметика;
 *   2. НЕПРОЗРАЧНЫЙ Limit<U> для лимитов — он не число, поэтому '>' с ним не компилируется
 *      вообще. Брендирования числа мало: реляционные операторы требуют лишь number-подобия,
 *      и utf16Len(s) > TEXT_MAX_CODEPOINTS прошло бы молча.
 */

import * as z from 'zod';

/* ── величины, с которыми нужна арифметика: брендированные числа ── */

export const Utf16Units = z.int().nonnegative().brand<'utf16'>();
export type Utf16Units = z.infer<typeof Utf16Units>;

export const CodePoints = z.int().nonnegative().brand<'codepoints'>();
export type CodePoints = z.infer<typeof CodePoints>;

export const Utf8Bytes = z.int().nonnegative().brand<'utf8bytes'>();
export type Utf8Bytes = z.infer<typeof Utf8Bytes>;

/* ── лимиты: непрозрачный тип, чтобы '>' с ним не компилировался ── */

declare const LimitBrand: unique symbol;
export type Limit<U> = { readonly [LimitBrand]: U };

/** docs/telegram.md §2: 4096 КОДПОИНТОВ, не UTF-16. */
export const TEXT_MAX_CODEPOINTS = 4096 as unknown as Limit<CodePoints>;
/** docs/telegram.md §8.1: 1024 кодпоинта. */
export const CAPTION_MAX_CODEPOINTS = 1024 as unknown as Limit<CodePoints>;
/** docs/telegram.md §2, ПРАВИЛО 2.3: 32768 БАЙТ utf-8, ошибка не транзиентная. */
export const REQUEST_MAX_UTF8_BYTES = 32768 as unknown as Limit<Utf8Bytes>;
/**
 * docs/telegram.md §9: у `sendRichMessage` потолок 32768 знаков. Для кириллицы он
 * недостижим — раньше упрётся REQUEST_MAX_UTF8_BYTES (два байта на символ), и это
 * правильный порядок: байтовый предел и есть настоящий.
 */
export const RICH_MAX_CODEPOINTS = 32768 as unknown as Limit<CodePoints>;
/**
 * ПОРОГ МАРШРУТИЗАЦИИ, а не лимит Telegram. Решение Ильи 06.09.2026
 * (docs/phase-2/08-visuals.md §1.2ж): на 32 знака ниже телеграмного потолка 4096.
 * Текст длиннее уходит `sendRichMessage` — и остаётся без карточки превью, потому что
 * параметра карточки у этого метода не существует. Предохранитель, а не режим:
 * коридоры форматов 900–2200.
 */
export const SEND_MESSAGE_MAX_CODEPOINTS = 4064 as unknown as Limit<CodePoints>;

export function exceeds<U extends number>(limit: Limit<U>, n: U): boolean {
  return (n as number) > (limit as unknown as number);
}

export function limitValue<U>(l: Limit<U>): number {
  return l as unknown as number;
}

/* ── измерения ── */

export function utf16Len(s: string): Utf16Units {
  return s.length as Utf16Units;
}

export function codePointLen(s: string): CodePoints {
  return [...s].length as CodePoints;
}

const UTF8_ENCODER = /* @__PURE__ */ new TextEncoder();

export function utf8Len(s: string): Utf8Bytes {
  return UTF8_ENCODER.encode(s).length as Utf8Bytes;
}

/**
 * Приведение на границе wire (Telegram отдаёт голые числа).
 * Без проверки — вызывать только после zod-парсинга.
 */
export function asUtf16(n: number): Utf16Units {
  return n as Utf16Units;
}

/**
 * Текст пригоден к кодированию в UTF-8 без потерь (нет одиночных суррогатов).
 * Проверено: TextEncoder даёт для 'test\uD83D' и для 'test�' ОДНИ И ТЕ ЖЕ байты,
 * то есть два разных текста получили бы один хэш. Чинить заменой запрещено —
 * docs/telegram.md ПРАВИЛО 4.5а.
 */
export function isWellFormedText(s: string): boolean {
  return s.isWellFormed();
}

/** i — граница символа, а не середина суррогатной пары. */
export function isCodeUnitBoundary(s: string, i: number): boolean {
  if (i <= 0 || i >= s.length) return true;
  const prev = s.charCodeAt(i - 1);
  const cur = s.charCodeAt(i);
  return !(prev >= 0xd800 && prev <= 0xdbff && cur >= 0xdc00 && cur <= 0xdfff);
}
